/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWBL } from '../../context/WBLContext';
import { formatPhoneNumber } from '../../utils/phone';
import { 
  Shield, 
  CheckCircle2, 
  UserCheck, 
  ArrowRight, 
  Lock, 
  Mail, 
  FileText, 
  Calendar, 
  GraduationCap, 
  AlertCircle, 
  Loader2, 
  X,
  Home,
  Check,
  PenTool,
  Type,
  RotateCcw,
  HelpCircle,
  MessageSquare,
  Scale,
  ShieldCheck,
  Users,
  LogIn,
  UserPlus,
  Sparkles,
  KeyRound
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { GlobalFooter } from '../Common/GlobalFooter';
import { Header } from '../Common/Header';

export const ParentOnboardingPage: React.FC = () => {
  const { 
    signUpWithRoleAndData, 
    signInWithEmail, 
    linkStudent, 
    activeUser, 
    users, 
    tenants, 
    getTenantBaseUrl, 
    changeTenant, 
    verifyStudentTicket,
    notifySuperadminOnFerpaConsent 
  } = useWBL();

  // ONLY Student Code and County/Region are allowed to be prefilled from URL query params
  const [studentCode, setStudentCode] = useState('');
  const [regionId, setRegionId] = useState('');

  // Explicitly DO NOT prefill date of birth, grad year (classYear), first name, or last name
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [classYear, setClassYear] = useState('');

  // Form step controls
  // 1 = Identify Student
  // 2 = FERPA Consent
  // 3 = Account Choice / Finish
  const [step, setStep] = useState(1);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  
  // Matched Student Details
  const [matchedStudent, setMatchedStudent] = useState<{
    uid: string;
    name: string;
    school: string;
    region: string;
    tenantId: string;
  } | null>(null);

  // FERPA consent details - DO NOT prefill names
  const [parentFirstName, setParentFirstName] = useState('');
  const [parentLastName, setParentLastName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [ferpaConsented, setFerpaConsented] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
  const [consentRecorded, setConsentRecorded] = useState(false);
  
  // Signature States
  const [signatureType, setSignatureType] = useState<'draw' | 'type'>('type');
  const [typedName, setTypedName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const sigPad = useRef<SignatureCanvas>(null);

  const clearSignature = () => {
    sigPad.current?.clear();
    setIsDrawing(false);
  };

  // Account creation & linking details
  const [password, setPassword] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [accountCreated, setAccountCreated] = useState(false);

  // Multi-student recognition state
  const [accountMode, setAccountMode] = useState<'new' | 'existing' | 'logged_in'>('new');
  const [isCheckingAccount, setIsCheckingAccount] = useState(false);
  const [existingParentInfo, setExistingParentInfo] = useState<{
    name?: string;
    email?: string;
    uid?: string;
    childrenCount?: number;
  } | null>(null);

  // Read URL parameters on mount - STRICT DISCIPLINE: ONLY prefill studentCode and regionId (county)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const codeParam = params.get('code') || params.get('studentCode') || '';
      const regionParam = params.get('region') || params.get('county') || '';
      
      if (codeParam) setStudentCode(codeParam.toUpperCase());
      if (regionParam) setRegionId(regionParam);
      // DO NOT prefill DOB, graduation year, or first/last names from URL parameters
    }
  }, []);

  const checkAccountForEmail = async (emailToCheck: string) => {
    if (!emailToCheck) return;
    setIsCheckingAccount(true);
    setAccountError(null);

    // 1. Check if user is currently logged in as parent
    if (activeUser && (activeUser.role === 'parent' || activeUser.email?.toLowerCase() === emailToCheck.toLowerCase())) {
      setAccountMode('logged_in');
      setExistingParentInfo({
        name: activeUser.name,
        email: activeUser.email,
        uid: activeUser.uid,
        childrenCount: (activeUser.childrenIds?.length || (activeUser.childId ? 1 : 0))
      });
      setIsCheckingAccount(false);
      return;
    }

    // 2. Check local users state
    const cleanEmail = emailToCheck.trim().toLowerCase();
    const localMatch = users.find(u => u.email && u.email.trim().toLowerCase() === cleanEmail);
    if (localMatch) {
      setAccountMode('existing');
      setExistingParentInfo({
        name: localMatch.name,
        email: localMatch.email,
        uid: localMatch.uid,
        childrenCount: (localMatch.childrenIds?.length || (localMatch.childId ? 1 : 0))
      });
      setIsCheckingAccount(false);
      return;
    }

    // 3. Query backend verification endpoint
    try {
      const response = await fetch('/api/check-parent-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail })
      });

      const contentType = response.headers.get('content-type') || '';
      if (response.ok && contentType.includes('application/json')) {
        const data = await response.json().catch(() => ({}));
        if (data.exists && data.user) {
          setAccountMode('existing');
          setExistingParentInfo({
            name: data.user.name,
            email: data.user.email,
            uid: data.user.uid,
            childrenCount: (data.user.childrenIds?.length || (data.user.childId ? 1 : 0))
          });
          setIsCheckingAccount(false);
          return;
        }
      }
    } catch (err) {
      console.warn('Account check note:', err);
    }

    // Default to new account if not found
    setAccountMode('new');
    setExistingParentInfo(null);
    setIsCheckingAccount(false);
  };

  const handleRedirectHome = () => {
    const targetId = regionId || matchedStudent?.tenantId || 'global';
    const isRegionSpecific = targetId && targetId !== 'global' && targetId !== 'apextech';

    if (typeof window !== 'undefined') {
      const targetPath = isRegionSpecific ? `/${targetId}` : '/';
      if (window.location.pathname !== targetPath) {
        window.location.href = targetPath;
      } else if (typeof (window as any).smoothNavigate === 'function') {
        (window as any).smoothNavigate(targetPath);
      } else {
        window.location.reload();
      }
    }
  };

  // Step 1: Submit verification to server
  const handleVerifyStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError(null);
    setIsVerifying(true);

    if (!studentCode.trim() || !dateOfBirth || !classYear.trim()) {
      setVerifyError('Please enter all required student identification fields.');
      setIsVerifying(false);
      return;
    }

    try {
      let matchedData: any = null;

      try {
        const response = await fetch('/api/verify-student', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentCode: studentCode.trim().toUpperCase(),
            dateOfBirth,
            classYear: classYear.trim(),
            region: regionId
          })
        });

        const contentType = response.headers.get('content-type') || '';
        if (response.ok && contentType.includes('application/json')) {
          const data = await response.json().catch(() => ({}));
          if (data.student) {
            matchedData = data.student;
          }
        }
      } catch (fetchErr) {
        console.warn('API verification attempt note:', fetchErr);
      }

      // Fallback verification via client context if backend had network/server issue
      if (!matchedData) {
        const foundStudentId = await verifyStudentTicket(dateOfBirth, studentCode.trim());
        if (foundStudentId) {
          matchedData = {
            uid: foundStudentId,
            name: foundStudentId === 'student-sam' ? 'Samuel Carter' : 'Student',
            school: 'High School WBL Program',
            region: regionId || 'Upstate Region',
            tenantId: regionId || 'spartanburg'
          };
        }
      }

      if (!matchedData) {
        throw new Error('No student found matching this access code and date of birth. Please check your credentials.');
      }

      setMatchedStudent(matchedData);
      if (matchedData.tenantId) {
        changeTenant(matchedData.tenantId);
      }
      
      setStep(2);
    } catch (err: any) {
      console.error(err);
      setVerifyError(err.message || 'Verification failed. Please check the credentials entered.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Step 2: Submit parent FERPA consent to server
  const handleRecordConsent = async (e: React.FormEvent) => {
    e.preventDefault();
    setConsentError(null);
    setIsSubmittingConsent(true);

    if (!parentFirstName.trim() || !parentLastName.trim() || !parentEmail.trim()) {
      setConsentError('Please provide your first name, last name, and email address.');
      setIsSubmittingConsent(false);
      return;
    }

    if (!ferpaConsented) {
      setConsentError('You must check the authorization box to grant FERPA consent.');
      setIsSubmittingConsent(false);
      return;
    }

    // Validate signature
    let signatureValue = '';
    const date = new Date().toISOString();

    if (signatureType === 'type') {
      if (!typedName.trim()) {
        setConsentError('Please type your full legal name to sign.');
        setIsSubmittingConsent(false);
        return;
      }
      signatureValue = typedName;
    } else {
      if (sigPad.current?.isEmpty()) {
        setConsentError('Please provide a signature on the pad.');
        setIsSubmittingConsent(false);
        return;
      }
      signatureValue = sigPad.current?.getCanvas().toDataURL('image/png') || '';
    }

    const PARENT_LEGAL_ATTESTATION = "I certify under penalty of perjury and law that I am the legal parent or court-appointed legal guardian of the minor student named herein and possess full legal authority to grant educational consent under FERPA (34 CFR § 99.30).";

    try {
      const parentName = `${parentFirstName.trim()} ${parentLastName.trim()}`;
      const parentAttestation = {
        certified: true,
        timestamp: date,
        statement: PARENT_LEGAL_ATTESTATION,
        signerName: typedName || parentName,
        signerEmail: parentEmail.trim().toLowerCase(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
      };

      const consentPayload = {
        ferpaConsent: {
          granted: true,
          date,
          version: '1.0',
          signatureType,
          signatureValue,
          parentAttestation
        },
        parentAttestation,
        ferpaConsentName: typedName || parentName,
        ferpaConsentDate: date,
        parentEmail: parentEmail.trim().toLowerCase(),
        ferpaConsentRecipientEmail: parentEmail.trim().toLowerCase(),
        ferpaConsentSentAt: date
      };

      // 1. Direct client write to Firestore for instant persistence
      if (db && matchedStudent?.uid) {
        try {
          await setDoc(doc(db, 'users', matchedStudent.uid), consentPayload, { merge: true });
        } catch (clientWriteErr) {
          console.warn('Client-side Firestore consent update note:', clientWriteErr);
        }
      }

      // 2. Submit to backend API
      const response = await fetch('/api/submit-parent-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentUid: matchedStudent?.uid,
          studentName: matchedStudent?.name,
          studentEmail: matchedStudent?.email,
          schoolName: matchedStudent?.school,
          schoolId: matchedStudent?.schoolId,
          additionalSchoolIds: matchedStudent?.additionalSchoolIds || [],
          additionalSchools: matchedStudent?.additionalSchools || [],
          tenantId: matchedStudent?.tenantId,
          parentName,
          parentEmail: parentEmail.trim().toLowerCase(),
          signatureData: {
            type: signatureType,
            value: signatureValue,
            name: typedName || parentName,
            date
          },
          parentAttestation
        })
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json().catch(() => ({})) : {};

      if (!response.ok) {
        throw new Error(data.error || 'Failed to record consent.');
      }

      if (matchedStudent) {
        setMatchedStudent({
          ...matchedStudent,
          ferpaConsent: consentPayload.ferpaConsent,
          ferpaConsentName: consentPayload.ferpaConsentName,
          ferpaConsentDate: consentPayload.ferpaConsentDate,
          parentEmail: consentPayload.parentEmail
        });
      }

      setConsentRecorded(true);
      setStep(3);
      checkAccountForEmail(parentEmail.trim().toLowerCase());
    } catch (err: any) {
      console.error(err);
      setConsentError(err.message || 'Failed to register your consent. Please try again.');
    } finally {
      setIsSubmittingConsent(false);
    }
  };

  // Step 3: Link Student to Active Parent Session
  const handleLinkToActiveSession = async () => {
    setAccountError(null);
    setIsCreatingAccount(true);

    try {
      if (!matchedStudent?.uid) throw new Error("Student data missing.");
      await linkStudent(matchedStudent.uid, matchedStudent.name, activeUser?.uid);
      
      try {
        await fetch('/api/link-student-to-parent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentUid: activeUser?.uid,
            parentEmail: activeUser?.email,
            studentUid: matchedStudent.uid,
            studentName: matchedStudent.name
          })
        });
      } catch (apiErr) {
        console.warn("Backend link notification note:", apiErr);
      }

      setAccountCreated(true);
      setTimeout(() => {
        handleRedirectHome();
      }, 2000);
    } catch (err: any) {
      console.error(err);
      setAccountError(err.message || 'Failed to link student to your parent account.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // Step 3: Sign in to Existing Parent Account and Link Student
  const handleSignInAndLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError(null);
    setIsCreatingAccount(true);

    if (!password) {
      setAccountError('Please enter your account password.');
      setIsCreatingAccount(false);
      return;
    }

    try {
      const emailToUse = parentEmail.trim().toLowerCase();
      
      await signInWithEmail(emailToUse, password);

      if (matchedStudent?.uid) {
        await linkStudent(matchedStudent.uid, matchedStudent.name, existingParentInfo?.uid);
        
        try {
          await fetch('/api/link-student-to-parent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              parentUid: existingParentInfo?.uid,
              parentEmail: emailToUse,
              studentUid: matchedStudent.uid,
              studentName: matchedStudent.name
            })
          });
        } catch (apiErr) {
          console.warn("Backend link notification note:", apiErr);
        }
      }

      setAccountCreated(true);
      setTimeout(() => {
        handleRedirectHome();
      }, 2000);
    } catch (err: any) {
      console.error(err);
      setAccountError(err.message || 'Failed to sign in and link student.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // Step 3: Create New Parent Account and Link Student
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountError(null);
    setIsCreatingAccount(true);

    if (!password || password.length < 6) {
      setAccountError('Please enter a password with at least 6 characters.');
      setIsCreatingAccount(false);
      return;
    }

    try {
      const parentName = `${parentFirstName.trim()} ${parentLastName.trim()}`;
      const newParentData = {
        name: parentName,
        email: parentEmail.trim().toLowerCase(),
        role: 'parent' as const,
        contactFirstName: parentFirstName.trim(),
        contactLastName: parentLastName.trim(),
        contactPhone: parentPhone.trim(),
        childId: matchedStudent?.uid || '',
        childrenIds: matchedStudent?.uid ? [matchedStudent.uid] : [],
        tenantId: matchedStudent?.tenantId || regionId || 'spartanburg',
        region: matchedStudent?.region || 'Upstate Region',
        profileComplete: true,
        verified: true
      };

      const newParentUser = await signUpWithRoleAndData(
        parentEmail.trim().toLowerCase(),
        password,
        'parent',
        newParentData
      );

      if (matchedStudent?.uid && newParentUser?.uid) {
        await linkStudent(matchedStudent.uid, matchedStudent.name, newParentUser.uid);
      }

      setAccountCreated(true);
      setTimeout(() => {
        handleRedirectHome();
      }, 2000);
    } catch (err: any) {
      console.error(err);
      setAccountError(err.message || 'Failed to create parent account. Please try again.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleSkipAccount = () => {
    handleRedirectHome();
  };

  return (
    <div className="min-h-screen bg-[#091526] text-slate-100 flex flex-col justify-between font-sans relative overflow-hidden">
      
      {/* Same Navigator Bar as Front Page & Application */}
      <Header />

      {/* Decorative Glow Elements matching Front Page Theme - strictly contained to avoid scroll below footer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-12 left-1/2 -translate-x-1/2 w-[1000px] h-[350px] bg-gradient-to-b from-[#0094c8]/15 via-[#38bdf8]/5 to-transparent blur-3xl" />
        <div className="absolute -bottom-48 -left-48 w-96 h-96 rounded-full bg-[#0094c8]/10 blur-3xl" />
        <div className="absolute -bottom-48 -right-48 w-96 h-96 rounded-full bg-[#b9e612]/10 blur-3xl" />
      </div>

      {/* Main Container */}
      <main className="flex-1 max-w-xl w-full mx-auto px-4 sm:px-6 py-10 flex flex-col justify-center relative z-10">
        <div className="bg-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 sm:p-10 shadow-2xl relative">
          
          {/* Progress Indicators matching Front Page Accent */}
          <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
            <div className="flex items-center space-x-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${step >= 1 ? 'bg-[#0094c8] text-white shadow-md shadow-[#0094c8]/20' : 'bg-slate-800 text-slate-500'}`}>
                {step > 1 ? <Check className="w-4 h-4" /> : '1'}
              </div>
              <span className={`text-[11px] font-black uppercase tracking-wider ${step === 1 ? 'text-[#38bdf8]' : 'text-slate-500'}`}>Identify Student</span>
            </div>
            <div className="flex-1 h-px bg-white/10 mx-3" />
            <div className="flex items-center space-x-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${step >= 2 ? 'bg-[#0094c8] text-white shadow-md shadow-[#0094c8]/20' : 'bg-slate-800 text-slate-500'}`}>
                {step > 2 ? <Check className="w-4 h-4" /> : '2'}
              </div>
              <span className={`text-[11px] font-black uppercase tracking-wider ${step === 2 ? 'text-[#38bdf8]' : 'text-slate-500'}`}>FERPA Consent</span>
            </div>
            <div className="flex-1 h-px bg-white/10 mx-3" />
            <div className="flex items-center space-x-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${step >= 3 ? 'bg-[#0094c8] text-white shadow-md shadow-[#0094c8]/20' : 'bg-slate-800 text-slate-500'}`}>
                3
              </div>
              <span className={`text-[11px] font-black uppercase tracking-wider ${step === 3 ? 'text-[#38bdf8]' : 'text-slate-500'}`}>Account</span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            
            {/* STEP 1: IDENTIFY STUDENT */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6"
              >
                <div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full border border-[#0094c8]/30 bg-[#0094c8]/10 text-[#38bdf8] text-[10px] font-extrabold uppercase tracking-widest mb-3">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>Parent & Guardian Gateway</span>
                  </div>
                  <h2 className="text-2xl font-extrabold text-white tracking-tight">Verify Student Connection</h2>
                  <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                    Enter your high school student's unique access code and county to link your guardian profile and review educational consent disclosures.
                  </p>
                </div>

                {verifyError && (
                  <div className="p-4 bg-red-950/50 border border-red-800/60 rounded-2xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-200 font-medium leading-relaxed">{verifyError}</p>
                  </div>
                )}

                <form onSubmit={handleVerifyStudent} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">
                      Student Specific Access Code <span className="text-[#38bdf8]">*</span>
                    </label>
                    <input 
                      type="text" 
                      required
                      placeholder="E.G. A8D3K9"
                      value={studentCode}
                      onChange={(e) => setStudentCode(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all uppercase placeholder:text-slate-600"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">
                      County / Region
                    </label>
                    <select
                      value={regionId}
                      onChange={(e) => setRegionId(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                    >
                      <option value="">Select County</option>
                      {tenants.filter(t => t.id !== 'global').map(tenant => (
                        <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">
                        Student Date of Birth <span className="text-[#38bdf8]">*</span>
                      </label>
                      <input 
                        type="date" 
                        required
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all placeholder:text-slate-600"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">
                        Student Graduation Year <span className="text-[#38bdf8]">*</span>
                      </label>
                      <select
                        required
                        value={classYear}
                        onChange={(e) => setClassYear(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                      >
                        <option value="">Select Year</option>
                        <option value="2026">2026</option>
                        <option value="2027">2027</option>
                        <option value="2028">2028</option>
                        <option value="2029">2029</option>
                        <option value="2030">2030</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isVerifying}
                    className="w-full mt-3 py-3.5 bg-gradient-to-r from-[#0094c8] to-[#00c6eb] hover:from-[#0083b2] hover:to-[#00b5d8] text-white rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all shadow-lg shadow-[#0094c8]/20 cursor-pointer flex items-center justify-center space-x-2"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>VERIFYING STUDENT...</span>
                      </>
                    ) : (
                      <>
                        <span>VERIFY STUDENT CONNECTION</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* STEP 2: FERPA CONSENT PROMPT */}
            {step === 2 && matchedStudent && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6"
              >
                {/* Verified Student Banner */}
                <div className="p-4 bg-emerald-950/40 border border-emerald-500/30 rounded-2xl flex items-center space-x-3.5">
                  <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg shrink-0">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[9px] font-black uppercase text-emerald-400 tracking-wider block">Student Identity Linked</span>
                    <h4 className="text-sm font-extrabold text-white truncate">{matchedStudent.name}</h4>
                    <span className="text-[10px] text-slate-300 block truncate">{matchedStudent.school} &bull; {matchedStudent.region}</span>
                  </div>
                </div>

                <div>
                  <h2 className="text-xl font-black text-white tracking-tight">FERPA Consent Agreement</h2>
                  <p className="text-xs text-slate-300 leading-relaxed mt-1">
                    Please read and endorse this statutory educational consent document to authorize student program placements.
                  </p>
                </div>

                {consentError && (
                  <div className="p-4 bg-red-950/50 border border-red-800/60 rounded-2xl flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-red-200 font-medium leading-relaxed">{consentError}</p>
                  </div>
                )}

                {/* Consent statutory scrolling card */}
                <div className="p-5 bg-slate-950/90 rounded-2xl border border-white/10 text-[10px] leading-relaxed text-slate-300 h-64 overflow-y-auto space-y-4 font-sans scrollbar-thin scrollbar-thumb-slate-700">
                  <div className="flex items-center space-x-2 border-b border-white/10 pb-3 mb-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    <p className="font-black text-white text-[10px] uppercase tracking-widest">Family Educational Rights and Privacy Act (FERPA) Notice</p>
                  </div>
                  
                  <p className="text-[10px] leading-relaxed">
                    This document serves as the formal written consent required under the Family Educational Rights and Privacy Act (FERPA) (20 U.S.C. § 1232g; 34 CFR Part 99). 
                    <br /><br />
                    <span className="font-bold underline italic text-white">For Students Under 18:</span> This form must be signed by a Parent or Legal Guardian.
                    <br />
                    <span className="font-bold underline italic text-white">For Students 18 or Older:</span> This form must be signed by the Student (the "Eligible Student").
                  </p>

                  <section className="space-y-2">
                    <h4 className="text-[10px] font-black text-[#38bdf8] uppercase tracking-wider">1. Identification of Parties</h4>
                    <p className="pl-4">
                      I hereby authorize the [Student’s School District] and the WBL Portal to disclose protected education records to the following "Authorized Recipients":
                    </p>
                    <ul className="list-disc pl-8 space-y-1">
                      <li><span className="text-white">Regional Workforce Administrators:</span> Sponsoring Regional Agencies and Chamber Partners.</li>
                      <li><span className="text-white">Educational Oversight:</span> Verified School and Career Advisors and Facilitators.</li>
                      <li><span className="text-white">Prospective Professional Partners:</span> Employers and Internship Providers approved by regional coordinators and explicitly selected by the student.</li>
                    </ul>
                  </section>

                  <section className="space-y-2">
                    <h4 className="text-[10px] font-black text-[#38bdf8] uppercase tracking-wider">2. Specific Records to be Disclosed</h4>
                    <ul className="list-disc pl-8 space-y-1">
                      <li><span className="text-white">Academic Credentials:</span> Full name, date of birth, school and district affiliation, enrollment status, and graduation year.</li>
                      <li><span className="text-white">Professional Identity:</span> Resume, career cluster interests, skills, and professional summaries.</li>
                      <li><span className="text-white">Workplace Performance:</span> Application status and qualitative evaluations.</li>
                    </ul>
                  </section>

                  <section className="space-y-2">
                    <h4 className="text-[10px] font-black text-[#38bdf8] uppercase tracking-wider">3. Purpose of Disclosure</h4>
                    <p className="pl-4">
                      This disclosure is made to facilitate WBL applications, monitor progress for academic credit, and ensure regional program compliance.
                    </p>
                  </section>
                </div>

                <form onSubmit={handleRecordConsent} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-300 tracking-wider">
                        Your First Name <span className="text-[#38bdf8]">*</span>
                      </label>
                      <input 
                        type="text" 
                        required
                        value={parentFirstName}
                        onChange={(e) => setParentFirstName(e.target.value)}
                        className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-300 tracking-wider">
                        Your Last Name <span className="text-[#38bdf8]">*</span>
                      </label>
                      <input 
                        type="text" 
                        required
                        value={parentLastName}
                        onChange={(e) => setParentLastName(e.target.value)}
                        className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-300 tracking-wider">
                        Your Email Address <span className="text-[#38bdf8]">*</span>
                      </label>
                      <input 
                        type="email" 
                        required
                        value={parentEmail}
                        onChange={(e) => setParentEmail(e.target.value)}
                        className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-300 tracking-wider">Your Phone Number</label>
                      <input 
                        type="tel" 
                        placeholder="(xxx) xxx-xxxx"
                        value={parentPhone}
                        onChange={(e) => setParentPhone(formatPhoneNumber(e.target.value))}
                        className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                      />
                    </div>
                  </div>

                  {/* Mandatory Parent/Guardian Legal Attestation Card */}
                  <div className="p-4 bg-amber-950/30 border border-amber-500/40 rounded-2xl space-y-2.5 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-amber-300 bg-amber-900/60 px-2.5 py-0.5 rounded-md border border-amber-500/40">
                        Parent/Guardian Legal Representation
                      </span>
                      <span className="text-[9px] font-mono font-bold text-amber-400 uppercase tracking-widest">
                        34 CFR § 99.30
                      </span>
                    </div>

                    <label htmlFor="ferpa-check" className="flex items-start space-x-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        id="ferpa-check"
                        checked={ferpaConsented}
                        onChange={(e) => setFerpaConsented(e.target.checked)}
                        className="mt-0.5 rounded border-amber-500/60 text-[#0094c8] focus:ring-[#38bdf8] w-4 h-4 bg-slate-900 accent-[#0094c8] shrink-0 cursor-pointer"
                      />
                      <span className="text-xs text-amber-100 font-semibold leading-relaxed select-none">
                        "I certify under penalty of perjury and law that I am the legal parent or court-appointed legal guardian of the minor student named herein and possess full legal authority to grant educational consent under FERPA (34 CFR § 99.30)."
                      </span>
                    </label>
                  </div>

                  {/* Signature Section */}
                  <div className="bg-slate-950/50 p-4 rounded-xl border border-white/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider">Digital Authorization</label>
                      <div className="flex bg-slate-900 rounded-lg border border-white/10 p-0.5">
                        <button 
                          type="button"
                          onClick={() => setSignatureType('type')}
                          className={`px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'type' ? 'bg-[#0094c8] text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          <Type className="w-3 h-3" />
                          Type
                        </button>
                        <button 
                          type="button"
                          onClick={() => setSignatureType('draw')}
                          className={`px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${signatureType === 'draw' ? 'bg-[#0094c8] text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'}`}
                        >
                          <PenTool className="w-3 h-3" />
                          Draw
                        </button>
                      </div>
                    </div>

                    {signatureType === 'type' ? (
                      <div className="space-y-2">
                        <input 
                          type="text"
                          placeholder="Type your full legal name to sign"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          className="w-full px-3 py-2.5 bg-slate-950 border border-white/15 rounded-xl text-xs font-bold text-white focus:ring-1 focus:ring-[#38bdf8] outline-none transition-all placeholder:text-slate-600"
                        />
                        <p className="text-[9px] text-slate-400 font-mono italic text-center">
                          Digitally Signed as: <span className="text-white font-bold underline">{typedName || '[Your Full Name]'}</span>
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="relative h-24 bg-white rounded-xl overflow-hidden group">
                          <SignatureCanvas 
                            ref={sigPad}
                            penColor="black"
                            canvasProps={{ className: 'w-full h-full cursor-crosshair' }}
                            onBegin={() => setIsDrawing(true)}
                          />
                          {!isDrawing && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Draw Signature Here</p>
                            </div>
                          )}
                          <button 
                            type="button"
                            onClick={clearSignature}
                            className="absolute bottom-2 right-2 p-1 bg-slate-100 text-slate-500 rounded-md hover:bg-slate-200 transition-all opacity-0 group-hover:opacity-100"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        </div>
                        <input 
                          type="text"
                          placeholder="Verify Printed Name"
                          value={typedName}
                          onChange={(e) => setTypedName(e.target.value)}
                          className="w-full px-3 py-2 bg-slate-950 border border-white/15 rounded-lg text-[10px] font-bold text-white focus:ring-1 focus:ring-[#38bdf8] outline-none transition-all placeholder:text-slate-600"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="px-4 py-3 bg-slate-950 text-slate-300 hover:text-white rounded-xl font-bold uppercase tracking-wider text-[10px] border border-white/15 transition-all cursor-pointer"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingConsent}
                      className="flex-1 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-600 hover:to-emerald-500 text-slate-950 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all shadow-lg shadow-emerald-500/20 cursor-pointer flex items-center justify-center space-x-2"
                    >
                      {isSubmittingConsent ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>RECORDING CONSENT...</span>
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4" />
                          <span>GRANT FERPA CONSENT</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* STEP 3: ACCOUNT CREATION / MULTI-STUDENT LINKING */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="space-y-6"
              >
                <div className="text-center py-2 space-y-3">
                  <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto border border-emerald-500/30 shadow-lg">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-white">FERPA Consent Saved!</h2>
                    <p className="text-xs text-slate-300 leading-relaxed max-w-sm mx-auto mt-1">
                      Your consent has been successfully transmitted and recorded on {matchedStudent?.name}'s student record.
                    </p>
                  </div>
                </div>

                {!accountCreated ? (
                  <div className="space-y-6">
                    {isCheckingAccount ? (
                      <div className="p-8 bg-slate-950/50 border border-white/10 rounded-2xl flex flex-col items-center justify-center space-y-3 text-center">
                        <Loader2 className="w-6 h-6 text-[#38bdf8] animate-spin" />
                        <p className="text-xs font-bold text-slate-300 tracking-wide">Checking guardian account status...</p>
                      </div>
                    ) : accountMode === 'logged_in' ? (
                      /* ACTIVE LOGGED-IN GUARDIAN SESSION */
                      <div className="space-y-4">
                        <div className="p-5 bg-gradient-to-br from-cyan-950/40 via-slate-950/60 to-blue-950/40 border border-[#0094c8]/30 rounded-2xl space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="px-2.5 py-1 bg-[#0094c8]/20 text-[#38bdf8] text-[9px] font-black uppercase tracking-wider rounded-lg border border-[#0094c8]/30 flex items-center gap-1.5">
                              <Sparkles className="w-3 h-3" />
                              Active Guardian Account
                            </span>
                            <span className="text-[10px] text-slate-300 font-mono">{activeUser?.email}</span>
                          </div>
                          <div>
                            <h3 className="text-sm font-black text-white">Add {matchedStudent?.name} to Your Family Account</h3>
                            <p className="text-[11px] text-slate-300 mt-1 leading-relaxed">
                              You are currently signed in as <strong className="text-white">{activeUser?.name}</strong>. Add <strong className="text-[#38bdf8]">{matchedStudent?.name}</strong> to manage their attendance and work placements from your central Guardian Console.
                            </p>
                          </div>
                        </div>

                        {accountError && (
                          <div className="p-4 bg-red-950/50 border border-red-800/60 rounded-2xl flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-200 font-medium leading-relaxed">{accountError}</p>
                          </div>
                        )}

                        <div className="flex flex-col space-y-2 pt-2">
                          <button
                            type="button"
                            onClick={handleLinkToActiveSession}
                            disabled={isCreatingAccount}
                            className="w-full py-3.5 bg-gradient-to-r from-[#0094c8] to-[#00c6eb] hover:from-[#0083b2] hover:to-[#00b5d8] text-white rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all shadow-lg hover:shadow-cyan-500/20 cursor-pointer flex items-center justify-center space-x-2"
                          >
                            {isCreatingAccount ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>ADDING STUDENT...</span>
                              </>
                            ) : (
                              <>
                                <Users className="w-4 h-4" />
                                <span>ADD {matchedStudent?.name?.toUpperCase() || 'STUDENT'} TO MY ACCOUNT</span>
                              </>
                            )}
                          </button>

                          <div className="flex items-center justify-between pt-2 px-1">
                            <button
                              type="button"
                              onClick={() => {
                                setAccountMode('existing');
                                setPassword('');
                              }}
                              className="text-slate-300 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                            >
                              Sign in to another account
                            </button>

                            <button
                              type="button"
                              onClick={handleSkipAccount}
                              className="text-slate-400 hover:text-slate-300 text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                            >
                              Exit to home
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : accountMode === 'existing' ? (
                      /* RECOGNIZED EXISTING PARENT ACCOUNT */
                      <div className="space-y-5">
                        <div className="p-5 bg-gradient-to-br from-blue-950/40 via-slate-950/60 to-cyan-950/30 border border-blue-800/40 rounded-2xl space-y-2.5">
                          <div className="flex items-center justify-between">
                            <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 text-[9px] font-black uppercase tracking-wider rounded-lg border border-blue-500/30 flex items-center gap-1.5">
                              <KeyRound className="w-3 h-3" />
                              Parent Account Recognized
                            </span>
                          </div>
                          <div>
                            <h3 className="text-sm font-black text-white">Add Student to Your Existing Account</h3>
                            <p className="text-[11px] text-slate-300 mt-1 leading-relaxed">
                              We recognized an existing parent account for <strong className="text-white">{parentEmail}</strong>. Enter your password to sign in and link <strong className="text-[#38bdf8]">{matchedStudent?.name}</strong> to your dashboard.
                            </p>
                          </div>
                        </div>

                        {accountError && (
                          <div className="p-4 bg-red-950/50 border border-red-800/60 rounded-2xl flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-200 font-medium leading-relaxed">{accountError}</p>
                          </div>
                        )}

                        <form onSubmit={handleSignInAndLink} className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider">Account Email</label>
                            <input 
                              type="email" 
                              disabled
                              value={parentEmail}
                              className="w-full px-3 py-2.5 bg-slate-950/50 text-slate-400 font-bold border border-white/10 rounded-xl outline-none text-xs"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">Your Account Password</label>
                            <input 
                              type="password" 
                              required
                              placeholder="Enter your existing account password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                            />
                          </div>

                          <div className="flex flex-col space-y-2 pt-2">
                            <button
                              type="submit"
                              disabled={isCreatingAccount}
                              className="w-full py-3.5 bg-gradient-to-r from-[#0094c8] to-[#00c6eb] hover:from-[#0083b2] hover:to-[#00b5d8] text-white rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all shadow-lg hover:shadow-cyan-500/20 cursor-pointer flex items-center justify-center space-x-2"
                            >
                              {isCreatingAccount ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>AUTHENTICATING & LINKING...</span>
                                </>
                              ) : (
                                <>
                                  <LogIn className="w-4 h-4" />
                                  <span>SIGN IN & ADD {matchedStudent?.name?.toUpperCase() || 'STUDENT'}</span>
                                </>
                              )}
                            </button>

                            <div className="flex items-center justify-between pt-2 px-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setAccountMode('new');
                                  setPassword('');
                                  setAccountError(null);
                                }}
                                className="text-slate-300 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <UserPlus className="w-3 h-3" />
                                Create a new account instead
                              </button>

                              <button
                                type="button"
                                onClick={handleSkipAccount}
                                className="text-slate-400 hover:text-slate-300 text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                              >
                                Exit now
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    ) : (
                      /* NEW PARENT ACCOUNT CREATION */
                      <div className="space-y-6">
                        <div className="p-4 bg-slate-950/60 border border-white/10 rounded-2xl">
                          <h3 className="text-xs font-black uppercase text-[#38bdf8] tracking-widest">Create a Parent Account (Recommended)</h3>
                          <p className="text-[11px] text-slate-300 mt-1.5 leading-relaxed">
                            A parent account allows you to securely monitor your child's weekly attendance timesheets, view placement details, and receive direct updates from their advisor.
                          </p>
                        </div>

                        {accountError && (
                          <div className="p-4 bg-red-950/50 border border-red-800/60 rounded-2xl flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-200 font-medium leading-relaxed">{accountError}</p>
                          </div>
                        )}

                        <form onSubmit={handleCreateAccount} className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider">Account Email</label>
                            <input 
                              type="email" 
                              disabled
                              value={parentEmail}
                              className="w-full px-3 py-2.5 bg-slate-950/50 text-slate-400 font-bold border border-white/10 rounded-xl outline-none text-xs"
                            />
                          </div>

                          <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-300 tracking-wider block">Set Account Password</label>
                            <input 
                              type="password" 
                              required
                              placeholder="At least 6 characters"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              className="w-full px-3 py-2.5 bg-slate-950/80 text-white font-bold border border-white/15 rounded-xl focus:border-[#38bdf8] focus:ring-1 focus:ring-[#38bdf8] outline-none text-xs transition-all"
                            />
                          </div>

                          <div className="flex flex-col space-y-2 pt-2">
                            <button
                              type="submit"
                              disabled={isCreatingAccount}
                              className="w-full py-3.5 bg-gradient-to-r from-[#0094c8] to-[#00c6eb] hover:from-[#0083b2] hover:to-[#00b5d8] text-white rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all shadow-lg hover:shadow-cyan-500/20 cursor-pointer flex items-center justify-center space-x-2"
                            >
                              {isCreatingAccount ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>CREATING ACCOUNT...</span>
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-4 h-4" />
                                  <span>CREATE PARENT ACCOUNT</span>
                                </>
                              )}
                            </button>

                            <div className="flex items-center justify-between pt-2 px-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setAccountMode('existing');
                                  setPassword('');
                                  setAccountError(null);
                                }}
                                className="text-slate-300 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <LogIn className="w-3 h-3" />
                                Already have an account? Sign in to link
                              </button>

                              <button
                                type="button"
                                onClick={handleSkipAccount}
                                className="text-slate-400 hover:text-slate-300 text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                              >
                                Exit now
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-6 bg-slate-950/80 rounded-2xl border border-white/15 text-center space-y-4"
                  >
                    <div className="w-10 h-10 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto border border-emerald-500/30">
                      <Check className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-white">Student Successfully Linked!</h4>
                      <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                        <strong className="text-white">{matchedStudent?.name}</strong> has been added to your Guardian Console. You can now monitor their work-based learning progress in real-time.
                      </p>
                    </div>
                    <div className="pt-2">
                      <button
                        onClick={handleRedirectHome}
                        className="px-6 py-2.5 bg-gradient-to-r from-[#0094c8] to-[#00c6eb] hover:from-[#0083b2] hover:to-[#00b5d8] text-white rounded-xl font-extrabold text-[10px] uppercase tracking-wider transition-all shadow-md cursor-pointer hover:shadow-cyan-500/20"
                      >
                        Go to Guardian Console
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

          </AnimatePresence>

        </div>
      </main>

      {/* Corporate Platform Footer */}
      <GlobalFooter className="z-10 border-t border-white/10" />
    </div>
  );
};
